import React from 'react'
import { useFormik } from 'formik'
import { FormattedMessage } from 'react-intl'
import { Button, InputAdornment, Collapse, Typography } from '@material-ui/core'
import DateFnsUtils from '@date-io/date-fns'
import arSA from 'date-fns/locale/ar-SA'
import enUS from 'date-fns/locale/en-US'
import codeTypes from '../../configurations/code-types'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import {
  codeCreateRequestFormValidation,
  codeUsageRequestFormValidation
} from '../../utils/validationSchema'
import { FormLoader } from '../../components/Loader/FormLoader'
import { formStyles } from '../../components/Invoice/Steps/FormStyles'
import FormSelectField from '../FormSelectField'
import FormTextField from '../FormTextField'
import FormEntityLoadField from '../FormEntityLoadField'
import FormDateField from '../FormDateField'
import CodeDetails from '../../components/CodesPageContent/CodeDetails'
import { ListingConsumer, ListingProvider } from '../../context/ListingContext'
import { SingleCodeConsumer, SingleCodeProvider } from '../../context/SingleCodeContext'
import { LanguageConsumer } from '../../context/LanguageContext'
import CenterFocusWeakIcon from '@material-ui/icons/CenterFocusWeak'
import CookiesStorageService from '../../Services/CookiesStorageService'
import LinkIcon from '@material-ui/icons/Link'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import Alert from '@material-ui/lab/Alert'


const CodeUsageForm = ({ currentData, onSubmit, locale, cancelDialog, currentAction, codeUsageRequestID, updateParentEntity, currentCodeLoading }) => {
  const classes = formStyles()

  const [codeType, setCodeType] = React.useState('GS1')
  const [itemObject, setItemObject] = React.useState({})

  const [requestType, setRequestType] = React.useState(!!Object.keys(currentData || {}).length || currentAction === 'edit' ? 'create' : 'reuse')

  const [viewParentCode, setViewParentCode] = React.useState(false)

  const [viewReuse, setViewReuse] = React.useState(!Object.keys(currentData || {}).length)
  const [viewCreate, setViewCreate] = React.useState(!!Object.keys(currentData || {}).length)

  const [codeParentFilter, setCodeParentFilter] = React.useState(null)

  const [dateFrom, setDateFrom] = React.useState(null)
  const [dateTo, setDateTo] = React.useState(null)

  const usageFormActions = useFormik({
    initialValues: {
      codeType: 'GS1',
      itemCode: '',
      comment: '',
      codeFilter: {
        CodeLookupValue: '',
        CodeName: '',
        OnlyActive: false
      },
    },
    validateOnBlur: false,
    validateOnChange: true,
    validateOnMount: true,
    isInitialValid: false,
    validationSchema: codeUsageRequestFormValidation,
    onSubmit: ((values, formikHelpers) => {
      onSubmit(values, formikHelpers, null, values.itemCode, requestType)
    })
  })

  const createFormActions = useFormik({
    initialValues: {
      codeType: 'EGS',
      itemCode: '',
      codeName: '',
      codeNameAr: '',
      description: '',
      descriptionAr: '',
      parentCode: '',

      activeFrom: null,
      activeTo: null,

      linkedCode: '',
      linkedCodeId: null,
      requestReason: ''

      // codeName: 'Test1',
      // codeNameAr: 'تيست 1',
      // codeType: 'EGS',
      // description: 'Test1',
      // descriptionAr: 'تيست 1',
      // itemCode: '111',
      // linkedCode: 'EG-100000053-Pr104',
      // linkedCodeId: null,
      // parentCode: '10000019',
      // requestReason: 'Test'
    },
    validateOnBlur: false,
    validateOnChange: true,
    validateOnMount: true,
    isInitialValid: false,
    validationSchema: codeCreateRequestFormValidation,
    onSubmit: ((values, formikHelpers) => {
      onSubmit(values, formikHelpers, null, values.itemCode ? `EG-${CookiesStorageService.getTaxpayerTRN()}-${values.itemCode}` : '', requestType, codeUsageRequestID, false, updateParentEntity)
    })
  })
  React.useEffect(() => {
      if (!!Object.keys(currentData || {}).length) {
        setRequestType('create')
        setCodeType(currentData?.details?.codeType || 'GS1')
        setCodeParentFilter(currentData?.parentCategory?.itemCode ? { 'CodeLookupValue': currentData?.parentCategory?.itemCode || '' } : null)
        let codeSeg = currentData?.details?.itemCode?.split('-', 3) || []
        createFormActions.setValues({
          codeType: 'EGS',
          itemCode: codeSeg[2] || '',
          codeName: currentData?.details?.codeName_en || '',
          codeNameAr: currentData?.details?.codeName_ar || '',
          description: currentData?.details?.description_en || '',
          descriptionAr: currentData?.details?.description_ar || '',
          parentCode: currentData?.parentCategory?.itemCode || '',

          activeFrom: currentData?.details?.activeFrom ? new Date(currentData?.details?.activeFrom) || null : null,
          activeTo: currentData?.details?.activeTo ? new Date(currentData?.details?.activeTo) || null : null,

          linkedCode: '',
          linkedCodeId: currentData?.details?.linkedCode || null,
          requestReason: ''
        })
        setDateFrom(currentData?.details?.activeFrom ? new Date(currentData?.details?.activeFrom) || null : null)
        setDateTo(currentData?.details?.activeTo ? new Date(currentData?.details?.activeTo) || null : null)
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentData])

  React.useEffect(() => {
      // formActions.setFieldValue('itemCode', '')
      setItemObject({})
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [codeType])

  const createEGSCode = () => {
    setItemObject({})
    setRequestType('create')
  }

  React.useEffect(() => {
      if (!!requestType) {
        switch (requestType) {
          case 'reuse':
            setViewReuse(true)
            setTimeout(() => {
              setViewCreate(false)
            }, 100)
            break
          case 'create':
            setViewCreate(true)
            setTimeout(() => {
              setViewReuse(false)
            }, 100)
            break
          default:
            break
        }
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestType])

  const handleDateChange = (date, fieldName, setDate) => {
    setDate(date)
    createFormActions.setFieldValue(fieldName, date || '')
  }


  return (
    <form onSubmit={requestType === 'create' ? createFormActions.handleSubmit : usageFormActions.handleSubmit}
          noValidate autoComplete="off" className={classes.formWrapper}>

      <Collapse in={requestType === 'reuse'} timeout="auto" unmountOnExit>
        {!!viewReuse && <>
          <FormSelectField name={'codeType'} labelKey={'Fields.label.view.Code.codeType'} list={codeTypes}
                           classes={classes} setFilter={setCodeType} formActions={usageFormActions} required small largeHeight/>

          <ListingProvider entityName={'codesSearch'} codeType={usageFormActions?.values?.codeType || 'GS1'}
                           codeTypeChanged={codeType} disableDefaultSearch
                           listingPaging={{
                             Ps: 100,
                             Pn: 1,
                             // OnlyActive: codeType === 'GS1' ? true : '',
                             ApplyMinChoiceLevel: true,
                             CodeTypeLevelNumber: 5
                           }}>
            <ListingConsumer>
              {({ listingData, listingLoading, handleListingFilterSubmit }) => (
                <FormEntityLoadField formActions={usageFormActions} classes={classes} name={'itemCode'}
                                     setReturnedObj={setItemObject} optionKey={'codeLookupValue'} large
                                     codeFilter={usageFormActions.values.codeFilter} codeType={codeType}
                                     entityKey={'codeLookupValue'} loading={listingLoading} list={listingData}
                                     searchable={'CodeLookupValue'} loadEntities={handleListingFilterSubmit}
                                     labelKey={'Fields.label.view.Code.itemCode'} required
                                     getOptionLabel={(option) => !!option?.codeLookupValue ? `${(locale === 'ar' ? option.codeNameSecondaryLang || option.codeNamePrimaryLang : option.codeNamePrimaryLang || option.codeNameSecondaryLang) || ''} (${option.codeLookupValue})` : '' }
                                     renderOption={(option, selected) => {
                                       return <React.Fragment>
                                         {!!option.codeLookupValue &&
                                         <div className={classes.optionWrapper}>
                                           <CenterFocusWeakIcon color={'primary'}/>
                                           <div className={classes.optionContainer}>

                                             <div className={classes.optionParent}>
                                               {(!!option.parentCodeNamePrimaryLang ||
                                                 (locale === 'ar' && !!option.parentCodeNameSecondaryLang)) ?
                                                 (locale === 'ar' ? option.parentCodeNameSecondaryLang || option.parentCodeNamePrimaryLang : option.parentCodeNamePrimaryLang)
                                                 : <FormattedMessage id={'codesSearch.NoParent'}/>}
                                             </div>
                                             <div className={classes.option}>
                                               {locale === 'ar' ? option.codeNameSecondaryLang || option.codeNamePrimaryLang : option.codeNamePrimaryLang}
                                               <span className={classes.optionID}>
                                                     ({option.codeLookupValue})
                                                   </span>
                                             </div>
                                           </div>
                                         </div>
                                         }
                                       </React.Fragment>
                                     }}/>
              )}
            </ListingConsumer>
          </ListingProvider>
          {codeType === 'EGS' &&
          <Alert severity="warning" className={classes.alert}>
            <Typography variant={'body2'}>
              <FormattedMessage id='createEGSMessage' values={{
                createLink: <Button size={'large'} variant={'text'} color={'primary'} disableRipple
                                    onClick={createEGSCode} className={classes.modifyLink}><FormattedMessage
                  id='createEGSMessage.Link'/></Button>
              }}/>
            </Typography>
          </Alert>

          }


          {!!Object.keys(itemObject || {}).length &&
          <SingleCodeProvider>
            <SingleCodeConsumer>
              {({ loadCodeData }) => (
                <LanguageConsumer>
                  {({ locale }) => (
                    <CodeDetails
                      code={itemObject?.codeLookupValue || ''}
                      type={itemObject?.codeTypeNamePrimaryLang || ''}
                      locale={locale}
                      getCodeData={loadCodeData}
                      reuse/>
                  )}
                </LanguageConsumer>
              )}
            </SingleCodeConsumer>
          </SingleCodeProvider>
          }

          <FormTextField name={'comment'} labelKey={'Fields.label.view.Code.comment'} classes={classes}
                         formActions={usageFormActions} row lines={2}/>
        </>}
      </Collapse>
      <Collapse in={requestType === 'create'} timeout="auto" unmountOnExit>
        {!!viewCreate &&
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale === 'ar' ? arSA : enUS}>
          <>
            <FormSelectField name={'codeType'} labelKey={'Fields.label.view.Code.codeType'}
                             list={codeTypes.filter(type => type.key === 'EGS')}
                             classes={classes} setFilter={setCodeType} formActions={createFormActions} required small
                             disabled/>
            <FormTextField name={'itemCode'} labelKey={'Fields.label.view.Code.itemCode'} classes={classes}
                           formActions={createFormActions} required large
                           InputAttributes={{
                             startAdornment: <InputAdornment
                               position="start">{`EG-${CookiesStorageService.getTaxpayerTRN()}-`}</InputAdornment>
                           }}/>

            <FormTextField name={'codeName'} labelKey={'Fields.label.view.Code.codeName_en'} classes={classes}
                           formActions={createFormActions} required/>
            <FormTextField name={'codeNameAr'} labelKey={'Fields.label.view.Code.codeName_ar'} classes={classes}
                           formActions={createFormActions} required/>

            <FormTextField name={'description'} labelKey={'Fields.label.view.Code.description_en'} classes={classes}
                           formActions={createFormActions} lines={2}/>
            <FormTextField name={'descriptionAr'} labelKey={'Fields.label.view.Code.description_ar'} classes={classes}
                           formActions={createFormActions} lines={2}/>

            <ListingProvider entityName={'codesSearch'} codeType={'GS1'}
                             codeTypeChanged={'GS1'} disableDefaultSearch
                             initFilter={codeParentFilter}
                             listingPaging={{
                               Ps: 100,
                               Pn: 1,
                               OnlyActive: true,
                               ApplyMinChoiceLevel: true,
                               CodeTypeLevelNumber: 4
                             }}>
              <ListingConsumer>
                {({ listingData, listingLoading, handleListingFilterSubmit }) => (
                  <FormEntityLoadField formActions={createFormActions} classes={classes} name={'parentCode'}
                                       setReturnedObj={setItemObject} optionKey={'codeLookupValue'} row selectFirst onlySearchBtn
                                       entityKey={'codeLookupValue'} loading={listingLoading} list={listingData}
                                       searchable={'CodeLookupValue'} loadEntities={handleListingFilterSubmit}
                                       labelKey={'Fields.label.view.Code.categoryGPC'} required codeSearch={'GS1'}
                                       getOptionLabel={(option) => !!option?.codeLookupValue ? `${locale === 'ar' ? option.codeNameSecondaryLang || option.codeNamePrimaryLang : option.codeNamePrimaryLang} (${option.codeLookupValue})` || '' : ''}
                                       renderOption={(option, selected) => {
                                         return <React.Fragment>
                                           {!!option.codeLookupValue &&
                                           <div className={classes.optionWrapper}>
                                             <CenterFocusWeakIcon color={'primary'}/>
                                             <div className={classes.optionContainer}>

                                               <div className={classes.optionParent}>
                                                 {(!!option.parentCodeNamePrimaryLang ||
                                                   (locale === 'ar' && !!option.parentCodeNameSecondaryLang)) ?
                                                   (locale === 'ar' ? option.parentCodeNameSecondaryLang || option.parentCodeNamePrimaryLang : option.parentCodeNamePrimaryLang)
                                                   : <FormattedMessage id={'codesSearch.NoParent'}/>}
                                               </div>
                                               <div className={classes.option}>
                                                 {locale === 'ar' ? option.codeNameSecondaryLang || option.codeNamePrimaryLang : option.codeNamePrimaryLang}
                                                 <span className={classes.optionID}>
                                                     ({option.codeLookupValue})
                                                   </span>
                                               </div>
                                             </div>
                                           </div>
                                           }
                                         </React.Fragment>
                                       }}/>
                )}
              </ListingConsumer>
            </ListingProvider>

            {!!Object.keys(itemObject || {}).length &&
            <div>
              <Button size={'large'} variant={'text'} color={'primary'} disableRipple
                      onClick={() => setViewParentCode(!viewParentCode)}
                      className={classes.modifyLink}><FormattedMessage
                id={`viewParentCode.${!!viewParentCode}`}/></Button>
              <Collapse in={!!viewParentCode} timeout="auto">
                <SingleCodeProvider>
                  <SingleCodeConsumer>
                    {({ loadCodeData }) => (
                      <LanguageConsumer>
                        {({ locale }) => (
                          <CodeDetails
                            code={itemObject?.codeLookupValue || ''}
                            type={itemObject?.codeTypeNamePrimaryLang || ''}
                            locale={locale}
                            getCodeData={loadCodeData}
                            reuse/>
                        )}
                      </LanguageConsumer>
                    )}
                  </SingleCodeConsumer>
                </SingleCodeProvider>
              </Collapse>
            </div>
            }

            <FormDateField name={'activeFrom'} labelKey={'Fields.label.view.Code.activeFrom'}
                           classes={classes} formActions={createFormActions} required
                           value={dateFrom} disablePast
                           handleChange={(date) => handleDateChange(date, 'activeFrom', setDateFrom)}/>
            <FormDateField name={'activeTo'} labelKey={'Fields.label.view.Code.activeTo'}
                           classes={classes} formActions={createFormActions}
                           value={dateTo} disablePast
                           atts={{
                             minDate: createFormActions?.values?.activeFrom ? new Date(createFormActions?.values?.activeFrom) : null
                           }}
                           handleChange={(date) => handleDateChange(date, 'activeTo', setDateTo)}/>

            <ListingProvider entityName={'codesSearch'} codeType={'EGS'}
                             codeTypeChanged={'EGS'} disableDefaultSearch
                             listingPaging={{
                               Ps: 100,
                               Pn: 1,
                               ApplyMinChoiceLevel: true,
                               CodeTypeLevelNumber: 5
                             }}>
              <ListingConsumer>
                {({ listingData, listingLoading, handleListingFilterSubmit }) => (
                  <FormEntityLoadField formActions={createFormActions} classes={classes} name={'linkedCode'}
                                       optionKey={'codeLookupValue'} row onlySearchBtn
                                       entityKey={'codeLookupValue'} loading={listingLoading} list={listingData}
                                       searchable={'CodeLookupValue'} loadEntities={handleListingFilterSubmit}
                                       labelKey={'Fields.label.view.Code.linkedCode.label'} codeSearch={'EGS'}
                                       getOptionLabel={(option) => !!option?.codeLookupValue ? `${locale === 'ar' ? option.codeNameSecondaryLang || option.codeNamePrimaryLang : option.codeNamePrimaryLang} (${option.codeLookupValue})` || '' : ''}
                                       renderOption={(option, selected) => {
                                         return <React.Fragment>
                                           {!!option.codeLookupValue &&
                                           <div className={classes.optionWrapper}>
                                             <CenterFocusWeakIcon color={'primary'}/>
                                             <div className={classes.optionContainer}>

                                               <div className={classes.optionParent}>
                                                 {(!!option.parentCodeNamePrimaryLang ||
                                                   (locale === 'ar' && !!option.parentCodeNameSecondaryLang)) ?
                                                   (locale === 'ar' ? option.parentCodeNameSecondaryLang || option.parentCodeNamePrimaryLang : option.parentCodeNamePrimaryLang)
                                                   : <FormattedMessage id={'codesSearch.NoParent'}/>}
                                               </div>
                                               <div className={classes.option}>
                                                 {locale === 'ar' ? option.codeNameSecondaryLang || option.codeNamePrimaryLang : option.codeNamePrimaryLang}
                                                 <span className={classes.optionID}>
                                                     ({option.codeLookupValue})
                                                   </span>
                                               </div>
                                             </div>
                                           </div>
                                           }
                                         </React.Fragment>
                                       }}/>
                )}
              </ListingConsumer>
            </ListingProvider>
            {!!createFormActions.values.linkedCodeId &&
            <Typography paragraph variant={'body2'} className={classes.createEGSLinkWrapper}>
              <LinkIcon color={'secondary'}/><FormattedMessage id='oldLinkedCodeID' values={{
              code: createFormActions.values.linkedCodeId,
              removeLink: <Button size={'small'} variant={'outlined'} color={'secondary'} disableRipple
                                  onClick={() => createFormActions.setFieldValue('linkedCodeId', '')}><HighlightOffIcon/>
                <FormattedMessage
                  id='oldLinkedCodeID.RemoveLink'/></Button>
            }}/>
            </Typography>}

            <FormTextField name={'requestReason'} labelKey={'Fields.label.view.Code.requestReason'} classes={classes}
                           formActions={createFormActions} row lines={2}/>

          </>
        </MuiPickersUtilsProvider>}
      </Collapse>

      <div className={classes.FormSubmitRow}>
        <Button type="submit" variant='contained' size={'large'} color={'secondary'}
                onClick={requestType === 'create' ? createFormActions.handleSubmit : usageFormActions.handleSubmit}
                disabled={requestType === 'create' ? (createFormActions.isSubmitting || !createFormActions.isValid) : (usageFormActions.isSubmitting || !usageFormActions.isValid)}
        >
          <FormattedMessage id={`AddNewDialog.AddNewRecord.${currentAction}`}/>
        </Button>
        <Button variant='outlined' color='secondary'
                onClick={cancelDialog} className={'cancel'}>
          <FormattedMessage id='invoiceSteps.Cancel'/>
        </Button>
      </div>
      {(requestType === 'create' ? (!!createFormActions.isSubmitting || !!currentCodeLoading) : !!usageFormActions.isSubmitting) &&
      <FormLoader loading={true}/>}
    </form>
  )
}

export default CodeUsageForm
